'use client';

import { useEffect, useState } from 'react';

export type BackgroundEffectType = 'none' | 'accent-glow' | 'gradient-mesh' | 'aurora' | 'grid' | 'particles' | 'vignette';

interface V2BackgroundEffectProps {
  effect: BackgroundEffectType;
}

export function V2BackgroundEffect({ effect }: V2BackgroundEffectProps) {
  // Render all effects client-only to avoid hydration mismatches
  // (color-mix() CSS values normalize differently on server vs client)
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  if (effect === 'none' || !mounted) return null;

  return (
    <div
      className="pointer-events-none fixed top-0 left-0"
      style={{ zIndex: 0, overflow: 'hidden', width: '100vw', height: '100vh' }}
      aria-hidden="true"
    >
      {effect === 'accent-glow' && <AccentGlow />}
      {effect === 'gradient-mesh' && <GradientMesh />}
      {effect === 'aurora' && <Aurora />}
      {effect === 'grid' && <GridEffect />}
      {effect === 'particles' && <Particles />}
      {effect === 'vignette' && <Vignette />}
    </div>
  );
}

const abs0: React.CSSProperties = { position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 };

function AccentGlow() {
  return (
    <div
      style={{
        ...abs0,
        backgroundImage: [
          `radial-gradient(ellipse 70% 45% at 15% 0%, color-mix(in srgb, var(--theme-secondary, var(--fv-accent)) 50%, transparent) 0%, transparent 75%)`,
          `radial-gradient(ellipse 50% 30% at 80% 5%, color-mix(in srgb, var(--theme-secondary, var(--fv-accent)) 25%, transparent) 0%, transparent 60%)`,
          `radial-gradient(ellipse 40% 25% at 50% 50%, color-mix(in srgb, var(--theme-secondary, var(--fv-accent)) 10%, transparent) 0%, transparent 70%)`,
        ].join(', '),
      }}
    />
  );
}

function GradientMesh() {
  useAnimationStyles('v2-mesh', `
    @keyframes v2-mesh-shift {
      0% { transform: scale(1) translate(0, 0); }
      50% { transform: scale(1.04) translate(1%, -1%); }
      100% { transform: scale(0.98) translate(-1%, 1%); }
    }
  `);

  return (
    <div
      style={{
        ...abs0,
        backgroundImage: [
          `radial-gradient(ellipse 55% 45% at 15% 15%, color-mix(in srgb, var(--theme-secondary, var(--fv-accent)) 40%, transparent) 0%, transparent 65%)`,
          `radial-gradient(ellipse 40% 40% at 85% 20%, color-mix(in srgb, var(--theme-secondary, var(--fv-accent)) 30%, transparent) 0%, transparent 55%)`,
          `radial-gradient(ellipse 45% 35% at 60% 65%, color-mix(in srgb, var(--theme-secondary, var(--fv-accent)) 25%, transparent) 0%, transparent 55%)`,
          `radial-gradient(ellipse 50% 40% at 20% 85%, color-mix(in srgb, var(--theme-secondary, var(--fv-accent)) 20%, transparent) 0%, transparent 60%)`,
        ].join(', '),
        animation: 'v2-mesh-shift 25s ease-in-out infinite alternate',
      }}
    />
  );
}

function Aurora() {
  useAnimationStyles('v2-aurora', `
    @keyframes v2-aurora-a {
      0% { transform: translate(0, 0) rotate(-5deg); }
      100% { transform: translate(15%, 10%) rotate(5deg); }
    }
    @keyframes v2-aurora-b {
      0% { transform: translate(0, 0) rotate(3deg); }
      100% { transform: translate(-10%, -8%) rotate(-4deg); }
    }
  `);

  const blobBase: React.CSSProperties = {
    position: 'absolute',
    borderRadius: '50%',
    filter: 'blur(100px)',
  };

  return (
    <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, overflow: 'hidden' }}>
      <div
        style={{
          ...blobBase,
          width: '60%',
          height: '50%',
          top: '-10%',
          left: '-15%',
          backgroundColor: 'color-mix(in srgb, var(--theme-secondary, var(--fv-accent)) 25%, transparent)',
          animation: 'v2-aurora-a 18s ease-in-out infinite alternate',
        }}
      />
      <div
        style={{
          ...blobBase,
          width: '50%',
          height: '40%',
          bottom: '-5%',
          right: '-10%',
          backgroundColor: 'color-mix(in srgb, var(--theme-secondary, var(--fv-accent)) 18%, transparent)',
          animation: 'v2-aurora-b 22s ease-in-out infinite alternate',
        }}
      />
    </div>
  );
}

function GridEffect() {
  return (
    <div
      style={{
        ...abs0,
        backgroundImage: [
          `linear-gradient(color-mix(in srgb, var(--theme-secondary, var(--fv-accent)) 25%, transparent) 1px, transparent 1px)`,
          `linear-gradient(90deg, color-mix(in srgb, var(--theme-secondary, var(--fv-accent)) 25%, transparent) 1px, transparent 1px)`,
        ].join(', '),
        backgroundSize: '60px 60px',
        maskImage: 'radial-gradient(ellipse 90% 80% at 50% 30%, black 30%, transparent 75%)',
        WebkitMaskImage: 'radial-gradient(ellipse 90% 80% at 50% 30%, black 30%, transparent 75%)',
      }}
    />
  );
}

function Particles() {
  useAnimationStyles('v2-particles', `
    @keyframes v2-particles-float {
      0% { background-position: 0 0, 13px 27px, 41px 7px, 5px 53px, 29px 11px; }
      100% { background-position: 73px -131px, 47px -97px, 19px -149px, 61px -83px, -37px -113px; }
    }
  `);

  return (
    <div
      style={{
        ...abs0,
        backgroundImage: [
          `radial-gradient(circle 2.5px, color-mix(in srgb, var(--theme-secondary, var(--fv-accent)) 65%, transparent) 0%, transparent 100%)`,
          `radial-gradient(circle 1.5px, color-mix(in srgb, var(--theme-secondary, var(--fv-accent)) 55%, transparent) 0%, transparent 100%)`,
          `radial-gradient(circle 2px, color-mix(in srgb, var(--theme-secondary, var(--fv-accent)) 45%, transparent) 0%, transparent 100%)`,
          `radial-gradient(circle 1px, color-mix(in srgb, var(--theme-secondary, var(--fv-accent)) 70%, transparent) 0%, transparent 100%)`,
          `radial-gradient(circle 1.5px, color-mix(in srgb, var(--theme-secondary, var(--fv-accent)) 50%, transparent) 0%, transparent 100%)`,
        ].join(', '),
        backgroundSize: '73px 89px, 47px 61px, 97px 53px, 31px 43px, 59px 71px',
        backgroundPosition: '0 0, 13px 27px, 41px 7px, 5px 53px, 29px 11px',
        animation: 'v2-particles-float 50s linear infinite',
      }}
    />
  );
}

function Vignette() {
  return (
    <div
      style={{
        ...abs0,
        backgroundImage: [
          `radial-gradient(ellipse 100% 100% at 50% 50%, transparent 30%, color-mix(in srgb, var(--theme-secondary, var(--fv-accent)) 25%, transparent) 100%)`,
          `radial-gradient(ellipse 50% 40% at 0% 0%, color-mix(in srgb, var(--theme-secondary, var(--fv-accent)) 35%, transparent) 0%, transparent 55%)`,
          `radial-gradient(ellipse 50% 40% at 100% 100%, color-mix(in srgb, var(--theme-secondary, var(--fv-accent)) 30%, transparent) 0%, transparent 55%)`,
        ].join(', '),
      }}
    />
  );
}

/** Inject @keyframes into <head> once on mount */
function useAnimationStyles(id: string, css: string) {
  useEffect(() => {
    const fullId = `v2-fx-${id}`;
    if (document.getElementById(fullId)) return;
    const style = document.createElement('style');
    style.id = fullId;
    style.textContent = css;
    document.head.appendChild(style);
  }, [id, css]);
}
